/**
 * Shared render-priority logic for product media (images + videos).
 *
 * Context: a product's image sources live in four fields on the `Product`
 * row — `mediaOverrides` (admin-curated), `images` (Sapo-synced, possibly
 * destructively overwritten), `backupImages` (Shopify-sourced fallback
 * from the backfill service), and `featuredImageUrl` (single string, also
 * from Sapo). Without a shared helper, every ProductCard / PDP / QuickView
 * rolled its own priority logic and some of them ignored `mediaOverrides`
 * entirely, which made admin-uploaded media invisible on the storefront.
 *
 * Priority order (highest to lowest):
 *   1. `mediaOverrides`   — admin hand-curated, always wins
 *   2. `images` (≥ 2)     — a "real" Sapo gallery beats the backup
 *   3. `backupImages`     — Shopify fallback for thin Sapo data
 *   4. `images` (1-5)     — thin Sapo gallery as last resort (no backup)
 *   5. `featuredImageUrl` — scalar fallback
 *
 * Client-safe (no server imports). Usable from any Remix loader or React
 * component.
 */

import { fixImgUrl } from "./storefront-constants";

export interface ResolvedMedia {
  url: string;
  alt?: string;
  color?: string | null;
  type?: "image" | "video";
}

export interface ProductMediaInput {
  mediaOverrides?: unknown;
  images?: unknown;
  backupImages?: unknown;
  featuredImageUrl?: string | null;
  title?: string | null;
}

// A Sapo gallery is considered "real" only when it has at least this many
// images. Below this threshold, we prefer the backup gallery (from Shopify)
// which typically has 8-12 images. Raised from 2 to 6 because many products
// had only 2-5 Sapo images while the Shopify originals had full galleries.
const MIN_SAPO_GALLERY = 6;

/**
 * Resolve a product's media list using the shared priority rules.
 *
 * Returns an empty array if no media of any kind exists. Callers that
 * need a single cover image should use `resolveFeaturedImage()` instead.
 *
 * Waterfall (most specific wins):
 *   1. mediaOverrides — admin-curated, always trumps everything.
 *   2. backupImages   — R2-mirrored via Cloudflare worker. Always preferred
 *                       over Sapo when present, because R2 URLs:
 *                         - hit our Cloudflare CDN (AVIF + edge cache)
 *                         - support the ?w=NNN resize the listing pages use
 *                         - don't depend on bizweb.dktcdn.net availability
 *                         - support _600x suffix transforms reliably
 *                       Sapo's bizweb CDN has random-UUID filenames that
 *                       often 404 when the listing tries to fetch the
 *                       `_600x` thumbnail variant (Sapo only generates
 *                       thumbnails for a subset of products). Even though
 *                       the raw URL works, the resized URL doesn't, so
 *                       relying on Sapo as primary breaks the card grid.
 *   3. images (Sapo)  — last resort. Only used when no R2 backup exists.
 *   4. featuredImageUrl — singleton cover, used when galleries are empty.
 *
 * Mid-2026-05 change: previously preferred Sapo when it had
 * MIN_SAPO_GALLERY ≥ 6 images and ≥ backup count. After the
 * bizweb-to-R2 mirror (scripts/mirror-sapo-to-r2.cjs) ran, every active
 * wholesale product has a complete R2 backup, so the Sapo preference
 * stopped being correct — it just kept us pinned to a slower CDN that
 * also breaks on resize. R2 backup wins outright now.
 */
export function resolveProductMedia(p: ProductMediaInput): ResolvedMedia[] {
  const overrides = normalizeList(p.mediaOverrides);
  if (overrides.length > 0) return dedup(overrides);

  const backup = normalizeList(p.backupImages);
  if (backup.length > 0) return dedup(backup);

  const sapo = normalizeList(p.images);
  if (sapo.length > 0) return dedup(sapo);

  if (p.featuredImageUrl) {
    return [{ url: fixImgUrl(p.featuredImageUrl), alt: p.title || "" }];
  }

  return [];
}

/**
 * Normalize a URL for dedup comparison by stripping CDN query params
 * (e.g. `?width=`, `?v=`). Two URLs that differ only in query string
 * are treated as the same image.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
}

/** Remove duplicate URLs (keeps first occurrence). Uses normalized URL comparison. */
function dedup(items: ResolvedMedia[]): ResolvedMedia[] {
  const seen = new Set<string>();
  return items.filter((m) => {
    const key = normalizeUrl(m.url);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Resolve a single "featured" cover image for the product. Returns an
 * empty string if the product has no media at all — callers render a
 * placeholder box in that case.
 */
export function resolveFeaturedImage(p: ProductMediaInput): string {
  return resolveProductMedia(p)[0]?.url ?? "";
}

/**
 * Like `resolveProductMedia` but also surfaces a `source` tag on each
 * item so the admin gallery UI can render a small "Sapo" / "Backup" /
 * "Override" / "Featured" badge per tile. The source reflects which
 * field the URL came from BEFORE dedup; if the same URL appears in
 * multiple sources, the one that wins the cascade is the one tagged
 * (override > sapo/backup > featured).
 */
export type MediaSource = "override" | "sapo" | "backup" | "featured";
export interface ResolvedMediaWithSource extends ResolvedMedia {
  source: MediaSource;
}

export function resolveProductMediaWithSource(p: ProductMediaInput): ResolvedMediaWithSource[] {
  const overrides = normalizeList(p.mediaOverrides).map((m) => ({ ...m, source: "override" as const }));
  if (overrides.length > 0) return dedupTagged(overrides);

  const sapo = normalizeList(p.images).map((m) => ({ ...m, source: "sapo" as const }));
  const backup = normalizeList(p.backupImages).map((m) => ({ ...m, source: "backup" as const }));

  // Mirror the resolveProductMedia rule but tag the source.
  if (sapo.length >= MIN_SAPO_GALLERY && sapo.length >= backup.length) {
    return dedupTagged(sapo);
  }
  if (backup.length > 0) return dedupTagged(backup);
  if (sapo.length > 0) return dedupTagged(sapo);
  if (p.featuredImageUrl) {
    return [{ url: fixImgUrl(p.featuredImageUrl), alt: p.title || "", source: "featured" }];
  }
  return [];
}

function dedupTagged(items: ResolvedMediaWithSource[]): ResolvedMediaWithSource[] {
  const seen = new Set<string>();
  return items.filter((m) => {
    let key = m.url;
    try {
      const u = new URL(m.url);
      key = u.origin + u.pathname;
    } catch { /* use raw url */ }
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Normalize a raw JSON blob (either a string[] of URLs or an object
 * array with `{url, ...}` entries) into a consistent `ResolvedMedia[]`.
 * Filters out falsy entries and runs every URL through `fixImgUrl` so
 * the caller never has to think about R2-vs-CDN rewriting.
 */
function normalizeList(raw: unknown): ResolvedMedia[] {
  if (!Array.isArray(raw)) return [];
  const out: ResolvedMedia[] = [];
  for (const entry of raw) {
    if (!entry) continue;
    if (typeof entry === "string") {
      const fixed = fixImgUrl(entry);
      if (fixed) out.push({ url: fixed });
      continue;
    }
    if (typeof entry === "object") {
      const e = entry as Record<string, unknown>;
      const rawUrl = typeof e.url === "string" ? e.url : undefined;
      if (!rawUrl) continue;
      const fixed = fixImgUrl(rawUrl);
      if (!fixed) continue;
      const alt =
        typeof e.alt === "string"
          ? e.alt
          : typeof e.altText === "string"
            ? (e.altText as string)
            : undefined;
      const color =
        typeof e.color === "string"
          ? (e.color as string)
          : e.color === null
            ? null
            : undefined;
      const type =
        e.type === "video" || e.type === "image"
          ? (e.type as "image" | "video")
          : undefined;
      out.push({ url: fixed, alt, color, type });
    }
  }
  return out;
}
